/**
 * Maps GET /api/yms/gate-details payload into the appointment shape used by check-in.
 */
export function mapGateDetailsToAppointment(raw) {
  if (raw == null || typeof raw !== "object") return null;

  const root =
    raw.body ??
    raw.data ??
    raw.payload ??
    raw.result ??
    raw.content ??
    raw;

  const nested =
    root.appointment ??
    root.appointmentDto ??
    root.appointmentDetails ??
    null;

  const carrier =
    root.appointmentCarrier ??
    root.carrierDetails ??
    root.carrier ??
    nested?.appointmentCarrier;

  const base =
    nested && typeof nested === "object"
      ? { ...root, ...nested }
      : { ...root };

  if (carrier && typeof carrier === "object") {
    base.appointmentCarrier = {
      ...(base.appointmentCarrier && typeof base.appointmentCarrier === "object"
        ? base.appointmentCarrier
        : {}),
      ...carrier,
    };
  }

  const id =
    base.appointmentId ??
    base.appointmentID ??
    base.appointment_id ??
    (typeof base.id === "number" ? base.id : undefined) ??
    base.id;

  if (id != null && base.appointmentId == null) base.appointmentId = id;

  if (base.shipmentNumber == null && base.shipment_number != null) {
    base.shipmentNumber = base.shipment_number;
  }
  if (base.shipmentNumber == null && base.shipment_ref != null) {
    base.shipmentNumber = base.shipment_ref;
  }
  if (base.shipmentNumber == null && base.shipmentRef != null) {
    base.shipmentNumber = base.shipmentRef;
  }
  if (base.shipmentNumber == null && base.shipmentNo != null) {
    base.shipmentNumber = base.shipmentNo;
  }

  if (base.appointmentNumber == null && base.appointment_number != null) {
    base.appointmentNumber = base.appointment_number;
  }
  if (base.appointmentNumber == null && base.appointmentRef != null) {
    base.appointmentNumber = base.appointmentRef;
  }
  if (base.trailerNumber == null && base.trailer_number != null) {
    base.trailerNumber = base.trailer_number;
  }
  if (base.dockNumber == null && base.dock_number != null) {
    base.dockNumber = base.dock_number;
  }
  if (base.dockId == null && base.dock_id != null) {
    base.dockId = base.dock_id;
  }
  if (base.dockId == null && base.currentDockId != null) {
    base.dockId = base.currentDockId;
  }
  if (base.dockId == null && base.current_dock_id != null) {
    base.dockId = base.current_dock_id;
  }
  if (base.dockId == null && base.dockNumber != null) {
    base.dockId = base.dockNumber;
  }
  if (base.doorNumber == null && base.door_number != null) {
    base.doorNumber = base.door_number;
  }
  if (base.bayNumber == null && base.bay_number != null) {
    base.bayNumber = base.bay_number;
  }
  if (base.bayName == null && base.bay_name != null) {
    base.bayName = base.bay_name;
  }
  if (base.cancelled == null && base.isCancelled != null) {
    base.cancelled = base.isCancelled;
  }

  if (base.carrierName == null && base.carrier_name != null) {
    base.carrierName = base.carrier_name;
  }
  // YardGateDetailsDTO.carrier is a plain display-name string (resolved server-side from
  // appointment/shipment/party/YMS-carrier fallbacks) — only the object-shaped case (nested
  // appointmentCarrier) was handled above; this was the missing plain-string case.
  if (base.carrierName == null && typeof base.carrier === "string" && base.carrier.trim()) {
    base.carrierName = base.carrier.trim();
  }

  if (base.location == null && base.location_label != null) {
    base.location = base.location_label;
  }
  if (base.location == null && base.locationLabel != null) {
    base.location = base.locationLabel;
  }

  if (base.warehouseRef == null && base.warehouse_ref != null) {
    base.warehouseRef = base.warehouse_ref;
  }
  if (base.warehouseRef == null && typeof base.warehouse === "string" && base.warehouse.trim()) {
    base.warehouseRef = base.warehouse.trim();
  }
  if (
    base.warehouseRef == null &&
    base.warehouse &&
    typeof base.warehouse === "object"
  ) {
    const w = base.warehouse;
    const ref =
      w.warehouseRef ??
      w.warehouse_ref ??
      w.locationRef ??
      w.location_ref ??
      (typeof w.code === "string" ? w.code : null) ??
      (typeof w.ref === "string" ? w.ref : null);
    if (ref != null && String(ref).trim()) {
      base.warehouseRef = String(ref).trim();
    }
  }

  if (base.warehouseName == null && base.warehouse_name != null) {
    base.warehouseName = String(base.warehouse_name).trim();
  }
  if (
    base.warehouseName == null &&
    base.warehouse &&
    typeof base.warehouse === "object"
  ) {
    const w = base.warehouse;
    const nm =
      w.locationName ??
      w.warehouseName ??
      w.name ??
      w.displayName ??
      (typeof w.label === "string" ? w.label : null);
    if (nm != null && String(nm).trim()) {
      base.warehouseName = String(nm).trim();
    }
  }

  return Object.keys(base).length ? base : null;
}

/**
 * Maps GET /api/trailers/gate-check-in/lookup → { appointment, eligibility }.
 * `eligibility` is passed through for inline messaging; `appointment` drives the check-in form.
 */
export function mapGateCheckInLookupResponse(raw) {
  if (raw == null || typeof raw !== "object") {
    return { appointment: null, eligibility: null };
  }
  const root = raw.body ?? raw.data ?? raw.payload ?? raw.result ?? raw.content ?? raw;
  const eligibility =
    root.eligibility ??
    root.eligibilityDto ??
    root.eligibilityResult ??
    null;
  const gateDetails =
    root.gateDetails ??
    root.gateDetailsDto ??
    root.yardGateDetails ??
    root.yardGateDetailsDto ??
    null;

  const appointmentSource =
    gateDetails && typeof gateDetails === "object"
      ? { ...root, ...gateDetails, gateDetails }
      : root;

  const appointment = mapGateDetailsToAppointment(appointmentSource);
  return { appointment, eligibility };
}

/** Value for POST /api/trailers — backend requires warehouseRef non-blank. */
export function resolveWarehouseRefFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const w =
    appt.warehouseRef ??
    appt.warehouse_ref ??
    appt.locWarehouseRef ??
    (typeof appt.warehouse === "string" ? appt.warehouse : null) ??
    appt.warehouse?.locationRef ??
    appt.warehouse?.locationRef ??
    appt.locationRef ??
    appt.locRef ??
    appt.loc_id_ref ??
    "";
  return String(w).trim();
}

/** True when value looks like a UUID (locationRef saved in name fields breaks yard settings API). */
export function looksLikeWarehouseRefUuid(value) {
  const s = String(value ?? "").trim();
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function readableWarehouseLabel(raw) {
  if (raw == null) return "";
  const s = String(raw).trim();
  if (!s || looksLikeWarehouseRefUuid(s)) return "";
  return s;
}

/**
 * Human-readable warehouse name for POST /api/trailers `warehouseRef` payload
 * (product expects display name, not locationRef / db id).
 */
export function resolveWarehouseNameForTrailer(appt) {
  if (!appt || typeof appt !== "object") return "";
  const wh = appt.warehouse && typeof appt.warehouse === "object" ? appt.warehouse : null;
  const candidates = [
    appt.warehouseName,
    appt.warehouse_name,
    appt.locWarehouseName,
    appt.loc_warehouse_name,
    appt.whLocationName,
    appt.warehouseLocationName,
    appt.selectedWarehouseName,
    wh?.locationName,
    wh?.warehouseName,
    wh?.name,
    wh?.displayName,
    typeof wh?.label === "string" ? wh.label : null,
    typeof appt.warehouse === "string" ? appt.warehouse : null,
  ];
  for (const c of candidates) {
    const label = readableWarehouseLabel(c);
    if (label) return label;
  }
  return "";
}

/** Office code on a location / warehouse DTO from GET /api/locations/*. */
export function resolveOfficeCodeFromLocation(loc) {
  if (!loc || typeof loc !== "object") return "";
  return String(
    loc.officeCode ??
      loc.office_code ??
      loc.parentLocation?.officeCode ??
      loc.parentLocation?.office_code ??
      loc.parent?.officeCode ??
      loc.parent?.office_code ??
      ""
  ).trim();
}

/** Office code carried on appointment / gate payloads when warehouse context is known. */
export function resolveOfficeCodeFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const wh = appt.warehouse && typeof appt.warehouse === "object" ? appt.warehouse : null;
  const candidates = [
    appt.officeCode,
    appt.office_code,
    appt.appointmentOfficeCode,
    appt.locOfficeCode,
    appt.originatorOfficeCode,
    wh?.officeCode,
    wh?.office_code,
    appt.location?.officeCode,
    appt.location?.office_code,
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (s) return s;
  }
  return "";
}

/**
 * Warehouse locationRef (UUID) for GET /api/dock/docks/{locationRef} — scoped to the appointment warehouse.
 */
export function resolveWarehouseLocationRefForDocks(appt, warehouseDisplayName = "") {
  const candidates = [
    resolveWarehouseRefFromAppointment(appt),
    resolveWarehouseLocationRefForNameLookup(appt),
    appt?.warehouse?.locationRef,
    appt?.warehouse?.location_ref,
    warehouseDisplayName,
  ];
  for (const c of candidates) {
    const s = String(c ?? "").trim();
    if (looksLikeWarehouseRefUuid(s)) return s;
  }
  return "";
}

/** Dock table primary key from GET /api/dock/docks/{warehouseRef} by display name. */
export function resolveWarehouseDockIdFromList(docks, dockName) {
  const target = String(dockName ?? "").trim();
  if (!target || !Array.isArray(docks)) return null;
  const matched = docks.find((dock) => {
    const name = String(dock.dockName ?? dock.name ?? "").trim();
    return name === target;
  });
  const raw = matched?.dockId ?? matched?.id;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/**
 * When the appointment carries a UUID (locationRef) but no display name yet, callers can GET /locations/by-loc-ref.
 */
export function resolveWarehouseLocationRefForNameLookup(appt) {
  if (!appt || typeof appt !== "object") return "";

  const tryValues = [
    resolveWarehouseRefFromAppointment(appt),
    String(appt.yardLocation ?? appt.yard_location ?? "").trim(),
    String(appt.warehouseName ?? appt.warehouse_name ?? "").trim(),
    typeof appt.warehouse === "string" ? appt.warehouse.trim() : "",
  ].filter(Boolean);

  for (const v of tryValues) {
    if (looksLikeWarehouseRefUuid(v)) return v;
  }
  return "";
}

/** Numeric dock id from appointment payloads (GET /api/appointment/{id}, gate lookup, etc.). */
export function resolveAppointmentDockId(appt) {
  if (!appt || typeof appt !== "object") return null;
  const raw =
    appt.dockId ??
    appt.dock_id ??
    appt.currentDockId ??
    appt.current_dock_id ??
    appt.dockNumber ??
    appt.dock_number ??
    appt.doorNumber ??
    appt.door_number ??
    appt.dock?.dockId ??
    appt.dock?.id ??
    null;
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

export function formatDockBay(appt) {
  if (!appt) return "";
  if (appt.bayName) return String(appt.bayName);
  const bay = appt.bayNumber ?? appt.bayId;
  const dock = appt.dockNumber ?? appt.doorNumber;
  if (bay != null && dock != null) return `Bay ${bay} / Dock ${dock}`;
  if (bay != null) return `Bay ${bay}`;
  if (dock != null) return `Dock ${dock}`;
  return "";
}

export function appointmentTitleId(appt) {
  if (!appt) return "";
  return (
    appt.appointmentNumber ??
    appt.appointmentRef ??
    appt.confirmationNumber ??
    appt.appointmentId ??
    ""
  );
}

/** Human / business shipment id for POST /api/trailers — complements form entry. */
export function resolveShipmentNumberFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const s =
    appt.shipmentNumber ??
    appt.shipment_number ??
    appt.shipmentRef ??
    appt.shipment_ref ??
    appt.shipmentNo ??
    "";
  return String(s).trim();
}

/** Display appointment ref for POST /api/trailers — backend maps to Appointment # column. */
export function resolveAppointmentNumberFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const n =
    appt.appointmentNumber ??
    appt.appointment_number ??
    appt.appointmentRef ??
    appt.confirmationNumber ??
    "";
  const trimmed = String(n ?? "").trim();
  if (trimmed) return trimmed;
  const id = appt.appointmentId ?? appt.appointmentID ?? appt.id;
  return id != null ? String(id) : "";
}

/** Carrier display name for POST /api/trailers — complements form entry. */
export function resolveCarrierNameFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const ac =
    appt.appointmentCarrier && typeof appt.appointmentCarrier === "object"
      ? appt.appointmentCarrier
      : {};
  const c =
    appt.carrierName ??
    ac.carrierName ??
    appt.carrier?.name ??
    ac.carrier?.name ??
    appt.carrier?.carrierName ??
    appt.carrierNameDisplay ??
    "";
  return String(c).trim();
}

/** Yard / dock location label for POST /api/trailers — complements Dock/Bay field. */
export function resolveLocationFromAppointment(appt) {
  if (!appt || typeof appt !== "object") return "";
  const fromDockBay = formatDockBay(appt);
  if (fromDockBay.trim()) return fromDockBay.trim();
  const loc =
    appt.location ??
    appt.locationLabel ??
    appt.gateLocation ??
    appt.yardLocation ??
    appt.dockLocation ??
    appt.bayLocation ??
    "";
  return String(loc).trim();
}

/**
 * LGX-1101 — walk-up freight. For this customer a trailer arriving with no pre-booked
 * appointment is the dominant case, not an edge case, so a zero-result search is the signal to
 * proceed in manual entry rather than a failure to report. This builds the blank draft the
 * check-in form opens on: no appointmentId (nothing to reference), the searched term carried
 * through for the inline note, and the term pre-filled into the field it corresponds to.
 *
 * A purely numeric term was an appointment-ID search that found nothing. A walk-up references
 * no appointment at all (LGX-1102), so there is no field on this form that term belongs in and
 * it is surfaced in the note only — writing it into Shipment Number would put a fabricated
 * shipment number onto the trailer record.
 */
export function buildManualCheckInDraft(searchTerm) {
  const term = String(searchTerm ?? "").trim();
  const searchedByAppointmentId = /^[0-9]+$/.test(term);

  return {
    appointmentId: null,
    manualEntry: true,
    manualSearchTerm: term,
    searchedByAppointmentId,
    shipmentNumber: searchedByAppointmentId ? "" : term,
  };
}

/** True when the check-in form is running as a walk-up (no backing appointment record). */
export function isManualCheckInDraft(appt) {
  return Boolean(appt && typeof appt === "object" && appt.manualEntry === true);
}
